import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { DesktopSidebar } from "./components/DesktopSidebar";
import { MobileSidebar } from "./components/MobileSidebar";
import livePreviewApi from "@/api/livePreview";
import { PageHelmet } from "@/components/PageHelmet/PageHelmet";
import { useAppDispatch } from "@/hooks/types";
import { LivePreview } from "@/pages/LivePreview/components/LivePreview";
import { templates } from "@/pages/LivePreview/constants";
import { IResumePayload } from "@/pages/LivePreview/types";
import { addNotification } from "@/store/app/slice";
import { NotificationMessageType, NotificationType } from "@/types/app";

const validateResumeUrl = (url: string) => {
    try {
        const parsedUrl = new URL(url);
        const whitelistedUrlPart = "1template.com";
        const path = parsedUrl.pathname;
        if (!path.includes(whitelistedUrlPart)) {
            throw new Error("Invalid domain");
        }
        return parsedUrl.toString();
    } catch {
        return false;
    }
};

const isValidStructure = (object: unknown): object is IResumePayload => {
    if (typeof object !== "object" || object === null) {
        return false;
    }
    try {
        const obj = object as Record<string, unknown>;
        return typeof obj.profile === "object" && obj.profile !== null;
    } catch {
        return false;
    }
};

export const LivePreviewPage = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [activeTemplateId, setActiveTemplateId] = useState<string | null>(
        templates[0].id,
    );

    const resumeUrl = new URLSearchParams(window.location.search).get(
        "resumeUrl",
    );
    const dispatch = useAppDispatch();

    const showNotification = (message: string) => {
        dispatch(
            addNotification({
                messageType: NotificationMessageType.LIVE_PREVIEW_ERROR,
                title: "Something went wrong",
                desc: message,
                type: NotificationType.ERROR,
            }),
        );
    };

    const resumeDataQuery = useQuery({
        queryKey: ["live-preview-resume-data", resumeUrl],
        queryFn: async () => {
            if (!resumeUrl) {
                return null;
            }

            const isValidUrl = validateResumeUrl(resumeUrl);
            if (!isValidUrl) {
                showNotification("Invalid resume URL");
                return null;
            }

            try {
                const response = await fetch(resumeUrl);
                const data = await response.json();

                if (!isValidStructure(data)) {
                    showNotification(
                        "Invalid data structure. Missing required fields.",
                    );
                    return null;
                }

                // Only track if we have valid data
                await livePreviewApi.trackLivePreview({
                    partnerId: "loopcv", // TODO: get partnerId dynamically
                    url: resumeUrl,
                    events: [
                        {
                            type: "preview",
                            timestamp: new Date(),
                        },
                    ],
                });

                return data;
            } catch (error) {
                showNotification("Invalid resume URL");
                return null;
            }
        },
        staleTime: 1000 * 60 * 60,
        gcTime: 1000 * 60 * 60,
    });

    return (
        <>
            <PageHelmet
                title="Live Preview"
                robotsOverride="noindex, nofollow"
            />
            <div>
                <MobileSidebar
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                    activeTemplateId={activeTemplateId}
                    setActiveTemplateId={setActiveTemplateId}
                />
                <DesktopSidebar
                    activeTemplateId={activeTemplateId}
                    setActiveTemplateId={setActiveTemplateId}
                />

                <main className="mx-2 py-0 lg:mx-0 lg:py-10 lg:pl-72">
                    <div className="overflow-x-auto whitespace-nowrap px-4 sm:px-6 lg:overflow-x-visible lg:whitespace-normal lg:px-8">
                        {activeTemplateId && (
                            <LivePreview
                                resumeUrl={resumeUrl}
                                resumeData={resumeDataQuery.data ?? null}
                                activeTemplateId={activeTemplateId}
                                loading={resumeDataQuery.isLoading}
                                error={resumeDataQuery.isError}
                            />
                        )}
                    </div>
                </main>
            </div>
        </>
    );
};
